/**
 * Builds, formats and compares the structured jurisdiction stored on Firm and Office.
 * Keep the copies in client/src/utils/jurisdiction and
 * sync_database_module/src/utils/jurisdiction identical (only import paths differ).
 */
import { IJurisdiction, JurisdictionLevel } from "./IJurisdiction";
import { COUNTRY_NAME_TO_ISO, ISO_TO_PREFERRED_COUNTRY_NAME } from "./countryIsoCodes";

export type JurisdictionFormat = "CITY_COUNTRY" | "COUNTRY_CITY" | "SHORT";

/** The only place where the visible jurisdiction format is chosen. */
export const DEFAULT_JURISDICTION_FORMAT: JurisdictionFormat = "CITY_COUNTRY";

/**
 * firm: Country, or StateProvince for state-based countries.
 * office: City, or StateProvince for state-based countries.
 */
export type JurisdictionScope = "firm" | "office";

export interface IJurisdictionParts {
  countryName?: string | null;
  stateName?: string | null;
  stateAbbreviation?: string | null;
  city?: string | null;
}

export type JurisdictionSource = {
  jurisdiction?: unknown;
  jurisdictionName?: unknown;
};

const STATE_BASED_COUNTRY_CODES = new Set(["US", "CA", "CN"]);

const LEVELS = new Set<JurisdictionLevel>(["Country", "StateProvince", "City"]);

const COUNTRY_ABBREVIATION_KEYS = new Set([
  "usa",
  "us",
  "united states",
  "uk",
  "great britain",
  "uae",
  "holland",
  "the netherlands",
  "prc",
]);

const GUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function normalizeJurisdictionKey(value: unknown): string {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\./g, "")
    .replace(/\s+/g, " ")
    .trim();
}

const ISO_BY_KEY = new Map<string, string>();
for (const name of Object.keys(COUNTRY_NAME_TO_ISO)) {
  ISO_BY_KEY.set(normalizeJurisdictionKey(name), COUNTRY_NAME_TO_ISO[name]);
}

function cleanText(value: unknown): string {
  if (typeof value !== "string") {
    return "";
  }
  return value.replace(/\s+/g, " ").trim();
}

export function looksLikeJurisdictionGuid(value: string): boolean {
  return GUID_REGEX.test(value.trim());
}

export function isStateBasedCountry(countryCode: string): boolean {
  return STATE_BASED_COUNTRY_CODES.has(countryCode.toUpperCase());
}

export function resolveCountryCode(countryName: unknown): string | null {
  const key = normalizeJurisdictionKey(countryName);
  if (!key) {
    return null;
  }
  return ISO_BY_KEY.get(key) ?? null;
}

/** Names and abbreviations that resolve to this ISO code. */
export function countrySearchNeedles(countryCode: string): string[] {
  const wanted = cleanText(countryCode).toUpperCase();
  if (!wanted) {
    return [];
  }
  const needles = new Set<string>();
  for (const [name, code] of Object.entries(COUNTRY_NAME_TO_ISO)) {
    if (code === wanted && name.trim().length >= 2) {
      needles.add(name.trim());
    }
  }
  return [...needles];
}

export function isJurisdiction(value: unknown): value is IJurisdiction {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  const record = value as Record<string, unknown>;
  return (
    LEVELS.has(record.level as JurisdictionLevel) &&
    cleanText(record.countryCode).length > 0 &&
    cleanText(record.countryName).length > 0
  );
}

function toTitleCase(value: string): string {
  return value
    .split(" ")
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : word))
    .join(" ");
}

/** Trims, drops an embedded trailing country, and title-cases all-upper / all-lower input. */
export function normalizeCityName(city: unknown, countryName?: string): string {
  let next = cleanText(city);
  const country = cleanText(countryName);
  if (next && country) {
    const suffix = normalizeJurisdictionKey(country);
    const parts = next.split(",").map((part) => part.trim());
    if (parts.length > 1 && normalizeJurisdictionKey(parts[parts.length - 1]) === suffix) {
      next = parts.slice(0, -1).join(", ").trim();
    }
  }
  if (next && (next === next.toLowerCase() || next === next.toUpperCase())) {
    next = toTitleCase(next);
  }
  return next;
}

function stateCode(abbreviation: unknown): string | undefined {
  const code = cleanText(abbreviation).toUpperCase();
  return /^[A-Z0-9]{1,3}$/.test(code) ? code : undefined;
}

/** Returns null when the country does not resolve to an ISO code. */
export function buildJurisdiction(
  parts: IJurisdictionParts,
  scope: JurisdictionScope,
): IJurisdiction | null {
  const countryName = cleanText(parts.countryName);
  const countryCode = resolveCountryCode(countryName);
  if (!countryName || !countryCode) {
    return null;
  }

  const stateName = cleanText(parts.stateName);
  const city = normalizeCityName(parts.city, countryName);
  const jurisdiction: IJurisdiction = { level: "Country", countryCode, countryName };
  const applyState = () => {
    jurisdiction.stateProvinceName = stateName;
    const code = stateCode(parts.stateAbbreviation);
    if (code) {
      jurisdiction.stateProvinceCode = code;
    }
  };

  if (isStateBasedCountry(countryCode) && stateName) {
    jurisdiction.level = "StateProvince";
    applyState();
    return jurisdiction;
  }
  if (scope === "firm") {
    return jurisdiction;
  }
  if (city) {
    jurisdiction.level = "City";
    jurisdiction.city = city;
    if (stateName) {
      applyState();
    }
    return jurisdiction;
  }
  if (stateName) {
    jurisdiction.level = "StateProvince";
    applyState();
  }
  return jurisdiction;
}

/** Trimmed copy without empty fields; null when the value is not a valid jurisdiction. */
export function sanitizeJurisdiction(value: unknown): IJurisdiction | null {
  if (!isJurisdiction(value)) {
    return null;
  }
  const countryCode = cleanText(value.countryCode).toUpperCase();
  if (!/^[A-Z]{2}$/.test(countryCode)) {
    return null;
  }
  const jurisdiction: IJurisdiction = {
    level: value.level,
    countryCode,
    countryName: cleanText(value.countryName),
  };
  const stateProvinceName = cleanText(value.stateProvinceName);
  const stateProvinceCode = cleanText(value.stateProvinceCode).toUpperCase();
  const city = normalizeCityName(value.city, jurisdiction.countryName);
  if (stateProvinceName) {
    jurisdiction.stateProvinceName = stateProvinceName;
  }
  if (stateProvinceCode) {
    jurisdiction.stateProvinceCode = stateProvinceCode;
  }
  if (city) {
    jurisdiction.city = city;
  }
  if (jurisdiction.level === "StateProvince" && !stateProvinceName) {
    return null;
  }
  if (jurisdiction.level === "City" && !city) {
    return null;
  }
  return jurisdiction;
}

function placeLabel(jurisdiction: IJurisdiction): string {
  if (jurisdiction.level === "City" && jurisdiction.city) {
    return jurisdiction.city;
  }
  if (jurisdiction.level === "StateProvince" && jurisdiction.stateProvinceName) {
    return jurisdiction.stateProvinceName;
  }
  return "";
}

export function formatJurisdiction(
  jurisdiction: IJurisdiction,
  format: JurisdictionFormat = DEFAULT_JURISDICTION_FORMAT,
): string {
  const place = placeLabel(jurisdiction);
  if (format === "SHORT") {
    return place ? `${place}, ${jurisdiction.countryCode}` : jurisdiction.countryCode;
  }
  if (!place) {
    return jurisdiction.countryName;
  }
  if (format === "COUNTRY_CITY") {
    return `${jurisdiction.countryName} – ${place}`;
  }
  return `${place}, ${jurisdiction.countryName}`;
}

/** Display text for a raw stored value: structured object or legacy label. GUIDs read as "". */
export function jurisdictionValueToLabel(value: unknown, format?: JurisdictionFormat): string {
  if (isJurisdiction(value)) {
    return formatJurisdiction(value, format);
  }
  const text = cleanText(value);
  if (!text || looksLikeJurisdictionGuid(text)) {
    return "";
  }
  return text;
}

/**
 * Display text for a Firm / Office. Prefers the stored jurisdictionName unless a
 * specific format is requested.
 */
export function getJurisdictionLabel(
  source: JurisdictionSource | null | undefined,
  format?: JurisdictionFormat,
): string {
  if (!source) {
    return "";
  }
  if (format && isJurisdiction(source.jurisdiction)) {
    return formatJurisdiction(source.jurisdiction, format);
  }
  const name = cleanText(source.jurisdictionName);
  if (name && !looksLikeJurisdictionGuid(name)) {
    return name;
  }
  return jurisdictionValueToLabel(source.jurisdiction, format);
}

/** Stable comparison key, independent of the display format. */
export function jurisdictionKey(jurisdiction: IJurisdiction): string {
  const country = jurisdiction.countryCode.toLowerCase();
  if (jurisdiction.level === "StateProvince") {
    return `${country}|${normalizeJurisdictionKey(jurisdiction.stateProvinceName)}`;
  }
  if (jurisdiction.level === "City") {
    return `${country}||${normalizeJurisdictionKey(jurisdiction.city)}`;
  }
  return country;
}

/** Key for a raw stored value; legacy strings fall back to their normalized text. */
export function jurisdictionValueKey(value: unknown): string {
  if (isJurisdiction(value)) {
    return jurisdictionKey(value);
  }
  const text = cleanText(value);
  return text ? `label:${normalizeJurisdictionKey(text)}` : "";
}

/** True when `inner` lies inside `outer` (same country, and same state / city when outer has one). */
export function jurisdictionContains(outer: unknown, inner: unknown): boolean {
  if (!isJurisdiction(outer) || !isJurisdiction(inner)) {
    return false;
  }
  if (outer.countryCode.toUpperCase() !== inner.countryCode.toUpperCase()) {
    return false;
  }
  if (outer.level === "Country") {
    return true;
  }
  if (outer.level === "StateProvince") {
    if (outer.stateProvinceCode && inner.stateProvinceCode) {
      return outer.stateProvinceCode.toUpperCase() === inner.stateProvinceCode.toUpperCase();
    }
    const outerState = normalizeJurisdictionKey(outer.stateProvinceName);
    return outerState.length > 0 && outerState === normalizeJurisdictionKey(inner.stateProvinceName);
  }
  const outerCity = normalizeJurisdictionKey(outer.city);
  return (
    inner.level === "City" &&
    outerCity.length > 0 &&
    outerCity === normalizeJurisdictionKey(inner.city)
  );
}

function countryNameFor(code: string, raw: string): string {
  const preferred = ISO_TO_PREFERRED_COUNTRY_NAME[code];
  if (preferred && COUNTRY_ABBREVIATION_KEYS.has(normalizeJurisdictionKey(raw))) {
    return preferred;
  }
  return raw;
}

function splitLabel(label: string): string[] {
  return label
    .split(/,|\s+[–—-]\s+/)
    .map((part) => part.replace(/\s+/g, " ").trim())
    .filter((part) => part.length > 0);
}

function fromCountryAndPlace(
  countryRaw: string,
  countryCode: string,
  place: string,
  scope: JurisdictionScope,
): IJurisdiction | null {
  const countryName = countryNameFor(countryCode, countryRaw);
  if (isStateBasedCountry(countryCode)) {
    return buildJurisdiction({ countryName, stateName: place }, scope);
  }
  return buildJurisdiction({ countryName, city: place }, scope);
}

/**
 * Parses a legacy label ("Arizona, USA", "United States of America, Arizona",
 * "Bangkok, Thailand", "Thailand"). Returns null when it cannot be resolved without
 * a human decision (unknown country, more than two parts, city with no country).
 */
export function parseJurisdictionLabel(
  label: unknown,
  scope: JurisdictionScope,
  fallbackCountryName?: string | null,
): IJurisdiction | null {
  const text = cleanText(label);
  if (!text || looksLikeJurisdictionGuid(text)) {
    return null;
  }
  const parts = splitLabel(text);
  if (parts.length === 1) {
    const code = resolveCountryCode(parts[0]);
    if (code) {
      return buildJurisdiction({ countryName: countryNameFor(code, parts[0]) }, scope);
    }
    const fallbackCode = resolveCountryCode(fallbackCountryName);
    if (fallbackCountryName && fallbackCode) {
      return fromCountryAndPlace(fallbackCountryName, fallbackCode, parts[0], scope);
    }
    return null;
  }
  if (parts.length !== 2) {
    return null;
  }
  const [left, right] = parts;
  const rightCode = resolveCountryCode(right);
  const leftCode = resolveCountryCode(left);
  if (rightCode && !leftCode) {
    return fromCountryAndPlace(right, rightCode, left, scope);
  }
  if (leftCode && !rightCode) {
    return fromCountryAndPlace(left, leftCode, right, scope);
  }
  return null;
}

/**
 * Jurisdiction to persist. Prefers an already structured value, then a legacy label.
 * Returns null when the value cannot be stored with a valid countryCode.
 */
export function storedJurisdictionFromSource(
  source: JurisdictionSource | null | undefined,
  scope: JurisdictionScope,
): IJurisdiction | null {
  if (!source) {
    return null;
  }
  const sanitized = sanitizeJurisdiction(source.jurisdiction);
  if (sanitized) {
    return sanitized;
  }
  const label =
    typeof source.jurisdiction === "string"
      ? source.jurisdiction
      : source.jurisdictionName;
  return parseJurisdictionLabel(label, scope);
}
